import React, { useMemo, useCallback, useState } from 'react';
import { View, StyleSheet, Platform, Pressable } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '@/context/ThemeContext';
import { useAuthStore, useModalStore } from '@/store';
import { useRouter } from 'expo-router';
import { Image } from 'expo-image';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import DropdownMenuItem from '@/components/DropdownMenu/DropdownMenuItem';
import DropdownMenuItemDivider from '@/components/DropdownMenu/DropdownMenuItemDivider';
import DropdownMenuLabel from '@/components/DropdownMenu/DropdownMenuLabel';
import useResponsiveness from '@/hooks/useResponsiveness';
import { trackEvent } from '@/constants/utils/analytics';
import { useTranslation } from '@/hooks/useTranslation';
import { checkIsIapSubscriber } from '@/lib/purchaseUtils';
import { formatCreditBalance } from '@/lib/formatCredits';

const createStyles = (colors, isXS,bottomInset) => StyleSheet.create({
  container: {
    width: '100%',
    borderTopWidth: 1,
    borderTopColor: colors.border_color_secondary,
    paddingLeft: isXS ? 20 : 10,
    paddingRight: isXS ? 10 : 10,
    paddingTop: 10,
    paddingBottom: 10 + bottomInset,
    minHeight: Platform.OS === 'macos' ? 54 : undefined,
  },
  triggerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    paddingVertical: 4,
    paddingLeft: Platform.OS === 'web' ? 5 : Platform.OS === 'macos' ? 10 : 0,
    borderRadius: 6,
    paddingRight: 
      Platform.OS === 'macos' ? 10 : 
      Platform.OS === 'web' ? 5 : 0,
    minHeight: Platform.OS === 'macos' ? 32 : undefined,
  },
  avatarContainer: {
    width: isXS ? 20 : Platform.OS === 'macos' ? 18 : 25,
    height: isXS ? 20 : Platform.OS === 'macos' ? 18 : 25,
    borderRadius: 16,
    backgroundColor: colors.solid_color_secondary,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  avatarImage: {
    width: '100%',
    height: '100%',
    borderRadius: 16,
  },
  avatarInitial: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.text_color_primary,
  },
  textContainer: {
    flex: 1,
    justifyContent: 'center',
  },
  ellipsisIcon: {
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
});

const HoverableTriggerRow = React.forwardRef(function HoverableTriggerRow({
  children,
  style,
  colors,
  accessibilityLabel,
  onMouseEnter,
  onMouseLeave,
  onHoverIn,
  onHoverOut,
  ...triggerProps
}, ref) {
  const [hovered, setHovered] = useState(false);
  const TriggerRow = Platform.OS === 'web' ? View : Pressable;
  return (
    <TriggerRow
      ref={ref}
      {...triggerProps}
      onMouseEnter={Platform.OS === 'web' ? (event) => {
        onMouseEnter?.(event);
        setHovered(true);
      } : onMouseEnter}
      onMouseLeave={Platform.OS === 'web' ? (event) => {
        onMouseLeave?.(event);
        setHovered(false);
      } : onMouseLeave}
      onHoverIn={Platform.OS !== 'web' ? (event) => {
        onHoverIn?.(event);
        setHovered(true);
      } : onHoverIn}
      onHoverOut={Platform.OS !== 'web' ? (event) => {
        onHoverOut?.(event);
        setHovered(false);
      } : onHoverOut}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      style={StyleSheet.flatten([style, hovered && { backgroundColor: colors.solid_color_tertiary }])}
    >
      {children}
    </TriggerRow>
  );
});

function SidebarAccountButton({ navigation, compact = false }) {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const router = useRouter();
  const { isXS } = useResponsiveness();
  const insets = useSafeAreaInsets();
  const styles = useMemo(() => createStyles(colors, isXS, insets.bottom), [colors, isXS, insets.bottom]);

  const email = useAuthStore(state => state.user?.email);
  const fullName = useAuthStore(state => state.user?.fullName);
  const avatarPath = useAuthStore(state => state.user?.avatarPath);
  const isPlus = useAuthStore(state => state.user?.flags?.isPlus);
  const isPlusPremium = useAuthStore(state => state.user?.flags?.isPlusPremium);
  const isGhost = useAuthStore(state => state.user?.flags?.isGhost);
  const activePurchase = useAuthStore(state => state.user?.activePurchase);
  const isIapSubscriber = useMemo(
    () => checkIsIapSubscriber(activePurchase),
    [activePurchase],
  );
  const totalAvailableCredits = useAuthStore(state => state.user?.credits?.totalAvailable ?? 0);
  const clearSession = useAuthStore(state => state.clearSession);
  const { openModal } = useModalStore(state => state.actions);

  const displayName = fullName || email || 'Account';
  const handle = email;

  // Get first letter for avatar initial
  const avatarInitial = useMemo(() => {
    if (fullName) return fullName.charAt(0).toUpperCase();
    if (email) return email.charAt(0).toUpperCase();
    return '?';
  }, [fullName, email]);

  const handleSettingsPress = useCallback(() => {
    if ((Platform.OS === 'web' || Platform.OS === 'macos') && !isXS) {
      openModal('SettingsModalWeb');
    } else {
      openModal('SettingsModalMobile');
    }
  }, [openModal, isXS]);

  const handleUpgradePress = useCallback(() => {
    trackEvent('conversion', 'upgrade_banner_clicked', 'sidebar_account_menu');
    openModal('PricingModal');
  }, [openModal]);

  // Plus Premium is web-only; never offer it on top of App Store / Play Plus.
  const canUpgradeToPremium = isPlus && !isPlusPremium && !isGhost && !isIapSubscriber && Platform.OS === 'web';

  const handleUpgradeToPremiumPress = useCallback(() => {
    trackEvent('conversion', 'premium_upgrade_clicked', 'sidebar_account_menu');
    openModal('PricingModal');
  }, [openModal]);

  const handleLogout = useCallback(async () => {
    try {
      await clearSession();
      if (Platform.OS === 'web') {
        router.replace('/');
      } else {
        router.replace('/login');
      }
    } catch (error) {
      console.error('Error during logout:', error);
    }
  }, [clearSession, router]);

  const handlePersonalizationPress = useCallback(() => {
    if ((Platform.OS === 'web' || Platform.OS === 'macos') && !isXS) {
      openModal('SettingsModalWeb', { tab: 'profile' });
    } else {
      openModal('SettingsModalMobile', { initialScreen: 'profile' });
    }
  }, [openModal, isXS]);

  const handleHelpPress = useCallback(() => {
    if ((Platform.OS === 'web' || Platform.OS === 'macos') && !isXS) {
      openModal('SettingsModalWeb', { tab: 'help' });
    } else {
      openModal('SettingsModalMobile', { initialScreen: 'help' });
    }
  }, [openModal, isXS]);

  const handleDownloadAppsPress = useCallback(() => {
    trackEvent('conversion', 'download_apps_menu_clicked', 'sidebar_account_menu');
    openModal('DownloadAppsModal');
  }, [openModal]);

  const isWeb = Platform.OS === 'web';

  return (
    <View style={[styles.container, compact && { paddingHorizontal: 8, paddingTop: 8, paddingBottom: 8 + insets.bottom, alignItems: 'center' }]}>
      <DropdownMenu.Root>
        {/* Native Zeego triggers replace their child's style with this prop. */}
        <DropdownMenu.Trigger asChild style={Platform.OS === 'macos' ? { width: compact ? 44 : '100%' } : StyleSheet.flatten([styles.triggerRow, compact && { width: 44, height: 44, paddingLeft: 0, paddingRight: 0, paddingVertical: 0, justifyContent: 'center', borderRadius: 11 }])}>
          <HoverableTriggerRow
            colors={colors}
            accessibilityLabel={t('sidebar.accountMenu')}
            // The macOS shim wraps its child instead of cloning its style.
            // Keep the row on the child so its text retains horizontal space.
            style={Platform.OS === 'macos' ? StyleSheet.flatten([styles.triggerRow, compact && { width: 44, height: 44, paddingLeft: 0, paddingRight: 0, paddingVertical: 0, justifyContent: 'center', borderRadius: 11 }]) : undefined}
          >
            <View style={[styles.avatarContainer, compact && { width: 28, height: 28 }]}>
              {avatarPath ? (
                <Image
                  source={{ uri: avatarPath }}
                  style={styles.avatarImage}
                  contentFit="cover"
                />
              ) : (
                <Text style={styles.avatarInitial}>{avatarInitial}</Text>
              )}
            </View>
            {!compact ? <View style={styles.textContainer}>
              <Text size="n" weight="medium" numberOfLines={1}>{displayName}</Text>
              {!isGhost && (
                <Text size="xs" color="tertiary" numberOfLines={1}>
                  {isPlusPremium ? 'Plus Premium' : (isPlus ? 'Plus' : 'Free')} · {formatCreditBalance(totalAvailableCredits)} credits
                </Text>
              )}
            </View> : null}
            {!compact ? <View style={styles.ellipsisIcon}>
              <Icon name="ellipsis" size={16} color={colors.text_color_secondary} />
            </View> : null}
          </HoverableTriggerRow>
        </DropdownMenu.Trigger>

        <DropdownMenu.Content
          className={Platform.OS === 'web' ? "DropdownMenuContent" : undefined}
          align="start"
          side="top"
          sideOffset={8}
        >
          {Platform.OS === 'web' ? (
            <>
              <DropdownMenuLabel text={handle || displayName} />
              <DropdownMenuItemDivider />
              {!isPlus && !isGhost && (
                <DropdownMenuItem
                  key="upgrade"
                  onSelect={handleUpgradePress}
                  icon="sparkles"
                  title={t('sidebar.upgradePlan')}
                />
              )}
              {canUpgradeToPremium && (
                <DropdownMenuItem
                  key="upgrade-premium"
                  onSelect={handleUpgradeToPremiumPress}
                  icon="gem"
                  title="Upgrade to Plus Premium"
                />
              )}
              <DropdownMenuItem
                key="personalization"
                onSelect={handlePersonalizationPress}
                icon="circle-user"
                title={t('sidebar.personalization')}
              />
              <DropdownMenuItem
                key="settings"
                onSelect={handleSettingsPress}
                icon="gear"
                title={t('sidebar.settings')}
              />
              <DropdownMenuItemDivider />
              {isWeb && (
                <DropdownMenuItem
                  key="download-apps"
                  onSelect={handleDownloadAppsPress}
                  icon="mobile-screen-button"
                  title={t('sidebar.downloadGabAiApp')}
                />
              )}
              <DropdownMenuItem
                key="help"
                onSelect={handleHelpPress}
                icon="circle-question"
                title={t('sidebar.help')}
              />
              <DropdownMenuItem
                key="logout"
                onSelect={handleLogout}
                icon="arrow-right-from-bracket"
                title={t('sidebar.logOut')}
              />
            </>
          ) : (
            <>
              <DropdownMenu.Item key="account-label" disabled>
                <DropdownMenu.ItemTitle>{handle || displayName}</DropdownMenu.ItemTitle>
              </DropdownMenu.Item>
              <DropdownMenu.Separator />
              {!isPlus && !isGhost && (
                <DropdownMenu.Item key="upgrade" onSelect={handleUpgradePress}>
                  <DropdownMenu.ItemIcon ios={{ name: 'sparkles' }} />
                  <DropdownMenu.ItemTitle>{t('sidebar.upgradePlan')}</DropdownMenu.ItemTitle>
                </DropdownMenu.Item>
              )}
              <DropdownMenu.Item key="personalization" onSelect={handlePersonalizationPress}>
                <DropdownMenu.ItemIcon ios={{ name: 'person.circle' }} />
                <DropdownMenu.ItemTitle>{t('sidebar.personalization')}</DropdownMenu.ItemTitle>
              </DropdownMenu.Item>
              <DropdownMenu.Item key="settings" onSelect={handleSettingsPress}>
                <DropdownMenu.ItemIcon ios={{ name: 'gearshape' }} />
                <DropdownMenu.ItemTitle>{t('sidebar.settings')}</DropdownMenu.ItemTitle>
              </DropdownMenu.Item>
              <DropdownMenu.Separator />
              <DropdownMenu.Item key="help" onSelect={handleHelpPress}>
                <DropdownMenu.ItemIcon ios={{ name: 'questionmark.circle' }} />
                <DropdownMenu.ItemTitle>{t('sidebar.help')}</DropdownMenu.ItemTitle>
              </DropdownMenu.Item>
              <DropdownMenu.Item key="logout" onSelect={handleLogout}>
                <DropdownMenu.ItemIcon ios={{ name: 'rectangle.portrait.and.arrow.right' }} />
                <DropdownMenu.ItemTitle>{t('sidebar.logOut')}</DropdownMenu.ItemTitle>
              </DropdownMenu.Item>
            </>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    </View>
  );
}

export default React.memo(SidebarAccountButton);
